import { getConfig } from '@/config/config';
import {
  type BaseQueryApi,
  type BaseQueryFn,
  type BaseQueryMeta,
  type FetchArgs,
  type FetchBaseQueryError,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import type { WebAPIQueryResult, WebApiResponseWithFailures } from './WebApiResponse.ts';
import { prepareHeaders } from './prepareHeader.ts';

const configuredFetchQuery = fetchBaseQuery({
  baseUrl: getConfig().apiUrl,
  prepareHeaders,
});

export const webApiResponseBaseQuery = async <T>(
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: object,
): Promise<WebAPIQueryResult<T>> => {
  const response = await (
    configuredFetchQuery as BaseQueryFn<
      string | FetchArgs,
      WebApiResponseWithFailures<T>,
      FetchBaseQueryError,
      BaseQueryMeta<BaseQueryFn>
    >
  )(args, api, extraOptions);

  if (response.error) {
    const errorData = (response.error as any)?.data;
    const failures = Array.isArray(errorData?.failures) && errorData.failures.length > 0
      ? errorData.failures
      : undefined;
    return {
      error: { type: 'httpFailures', ...response.error, ...(failures ? { failures } : {}) },
      meta: response.meta,
    }; // fetch error, passing through
  }

  // a 'backend global error' occurs when the first failure does not concern any field
  if (
    response.data.failures &&
    Array.isArray(response.data.failures) &&
    response.data.failures.length > 0 &&
    response.data.failures[0].fields.length === 0 &&
    response.data.data.length === 0 // functionnally we are in error mode only if there is no data
  ) {
    return {
      error: { type: 'functionalFailures', failures: response.data.failures },
      meta: response.meta,
    };
  }
  return response;
};
